/**
 * When a block on the marketing page is far enough into view to be allowed to
 * animate. Pure so it can be tested; the observer that calls it lives in
 * components/website/motion.ts.
 */

/**
 * How much of a block has to be showing. 0.45 with a bottom inset rather than
 * something flush and small: at 0.3 a tall card counts as in view while it is
 * still mostly below the fold, so by the time you have scrolled it into reading
 * position its animation has already finished and you see a resolved state.
 */
export const GATE_RATIO = 0.45;

/**
 * Thresholds to watch. A ratio alone is not enough, see below, so the observer
 * needs callbacks on the way in rather than only at 0.45: an element that can
 * never reach 0.45 would otherwise fire once at 0 and never again.
 */
export const GATE_THRESHOLDS = [0, 0.05, 0.15, 0.3, GATE_RATIO];

export interface GateReading {
  /** IntersectionObserverEntry.intersectionRatio */
  ratio: number;
  /** IntersectionObserverEntry.intersectionRect.height */
  visibleHeight: number;
  /** IntersectionObserverEntry.rootBounds.height, 0 when unknown */
  rootHeight: number;
}

/**
 * A ratio test on its own is unsatisfiable for a tall block. intersectionRatio
 * is measured against the ELEMENT, so a block 3x the height of the viewport
 * tops out at a ratio of 0.33 no matter how much of the screen it fills, and a
 * gate at 0.45 would hold it paused forever. Under the old restart-based gate
 * this was invisible, the animation had already played at load and the missed
 * callback just meant it was not replayed. Under a gate that has to release it,
 * the same miss is a block that never appears at all.
 *
 * So: released when it covers 45% of ITSELF, or 45% of the VIEWPORT. Short
 * blocks are governed by the first, tall ones by the second, and either way the
 * thing is properly on screen before it starts.
 */
export function isInViewEnough({ ratio, visibleHeight, rootHeight }: GateReading): boolean {
  if (ratio >= GATE_RATIO) return true;
  if (rootHeight > 0 && visibleHeight >= rootHeight * GATE_RATIO) return true;
  return false;
}
